import { FormEvent, useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { AlertTriangle, Eye, EyeOff } from "lucide-react";

import { Alert } from "../../components/Alert/Alert";
import { Avatar } from "../../components/Avatar/Avatar";
import { Badge } from "../../components/Badge/Badge";
import { Button } from "../../components/Button/Button";
import { Input } from "../../components/Input/Input";
import { useToast } from "../../components/Toast/Toast";
import { apiClient } from "../../lib/apiClient";
import { ApiError, describeError } from "../../lib/errors";

import { BrandMark } from "./BrandMark";
import { PasswordStrengthMeter } from "./PasswordStrengthMeter";
import { useAuth } from "./useAuth";
import styles from "./AuthLayout.module.css";

// Existing backend GET /accept response shape (internal/admin/api/handler/accept.go).
// The mockup spec calls for richer fields (invited_by, expires_at, exists,
// project: {id,name}); the impl currently exposes only the subset below.
// We keep that compatibility and derive the variant from `requires_signup`.
interface SummaryResponse {
  email: string;
  role: string;
  project_name?: string | null;
  game_id?: string | null;
  requires_signup: boolean;
  // Optional/forward-compat fields — backend may expose later (mockup L178).
  invited_by_email?: string | null;
  expires_at?: string | null;
}

type LoadState =
  | { kind: "loading" }
  | { kind: "invalid"; message: string }
  | { kind: "signup"; summary: SummaryResponse }
  | { kind: "existing"; summary: SummaryResponse };

export function AcceptInvitePage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const id = params.get("id");
  const token = params.get("token");
  const { user: me } = useAuth();
  const { push } = useToast();

  const [load, setLoad] = useState<LoadState>({ kind: "loading" });
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  useEffect(() => {
    if (!id || !token) {
      setLoad({
        kind: "invalid",
        message:
          "This invite link is missing required details. Ask the inviter to resend.",
      });
      return;
    }
    let cancelled = false;
    apiClient
      .get<SummaryResponse>(
        `/api/admin/accept?id=${encodeURIComponent(id)}&token=${encodeURIComponent(token)}`,
      )
      .then((r) => {
        if (cancelled) return;
        setLoad({
          kind: r.data.requires_signup ? "signup" : "existing",
          summary: r.data,
        });
      })
      .catch((err) => {
        if (cancelled) return;
        const message =
          err instanceof ApiError
            ? describeError(err.code)
            : "This invite link is invalid or expired. Ask the inviter to resend.";
        setLoad({ kind: "invalid", message });
      });
    return () => {
      cancelled = true;
    };
  }, [id, token]);

  // Variant C — Invalid
  if (load.kind === "invalid") {
    return (
      <div className={styles.page}>
        <div className={`${styles.card} ${styles.cardWide}`}>
          <BrandMark />
          <div className={styles.hero}>
            <div className={`${styles.heroIcon} ${styles.heroIconWarn}`}>
              <AlertTriangle size={32} />
            </div>
            <h1 className={styles.title}>This invite is no longer valid</h1>
            <div className={styles.subtitle}>
              The link may have expired (invites are valid for 72 hours), been
              revoked, or already accepted.
            </div>
            <div className={styles.infoBox}>
              <div>
                <span className={styles.title} style={{ display: "block" }}>
                  What to do
                </span>
                Ask the person who invited you to send a fresh invite. If
                you've already accepted,{" "}
                <Link to="/login" className={styles.link}>
                  sign in
                </Link>{" "}
                with your existing account.
              </div>
            </div>
            <div style={{ marginTop: 18 }}>
              <Button
                variant="secondary"
                size="lg"
                className={styles.submit}
                onClick={() => navigate("/login")}
              >
                Go to sign in
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (load.kind === "loading") {
    return (
      <div className={styles.page}>
        <div className={`${styles.card} ${styles.cardWide}`}>
          <BrandMark />
          <div className={styles.subtitle}>Checking your invite…</div>
        </div>
      </div>
    );
  }

  const projectName = load.summary.project_name ?? "your team";
  const role = load.summary.role;

  // Variant B — Existing user
  if (load.kind === "existing") {
    const signedIn = !!me;
    const emailMatch = signedIn && me?.email === load.summary.email;
    const nextPath = `/accept?id=${encodeURIComponent(id ?? "")}&token=${encodeURIComponent(token ?? "")}`;

    async function onAccept() {
      setActionError(null);
      setSubmitting(true);
      try {
        // Signed-in match: POST /accept/bind adds membership for current user.
        await apiClient.post("/api/admin/accept/bind", { id, token });
        navigate("/projects", { replace: true });
      } catch (err) {
        if (err instanceof ApiError) {
          setActionError(describeError(err.code));
        } else {
          setActionError("Something went wrong. Please try again.");
        }
      } finally {
        setSubmitting(false);
      }
    }

    async function onDecline() {
      setActionError(null);
      setSubmitting(true);
      try {
        // POST /accept/decline → 204; declines the invite for good.
        await apiClient.post("/api/admin/accept/decline", { id, token });
        push({ variant: "success", message: "Invite declined" });
        navigate("/login");
      } catch (err) {
        // 400 invalid_token → the invite is already resolved / expired; show
        // the invalid state. Any other failure surfaces an inline error.
        if (
          err instanceof ApiError &&
          (err.code === "invalid_token" || err.code === "expired")
        ) {
          setLoad({
            kind: "invalid",
            message: describeError("invalid_token"),
          });
          return;
        }
        setActionError(
          err instanceof ApiError
            ? describeError(err.code)
            : "Something went wrong. Please try again.",
        );
      } finally {
        setSubmitting(false);
      }
    }

    async function onSwitchAccount() {
      setActionError(null);
      try {
        await apiClient.post("/api/admin/auth/logout");
      } catch {
        // ignore — we redirect either way
      }
      navigate(`/login?next=${encodeURIComponent(nextPath)}`);
    }

    return (
      <div className={styles.page}>
        <div className={`${styles.card} ${styles.cardWide}`}>
          <BrandMark />
          <h1 className={styles.title}>Join {projectName}?</h1>
          <div className={styles.subtitle}>
            You've been invited to add this project to your account.
          </div>

          {actionError && (
            <div style={{ marginBottom: 14 }}>
              <Alert variant="danger" title="Couldn't accept invite">
                {actionError}
              </Alert>
            </div>
          )}

          {signedIn && (
            <div className={styles.signedInBar}>
              <Avatar name={me!.name || me!.email} size={22} />
              <span>
                Signed in as <span className={styles.name}>{me!.email}</span>
              </span>
              <button
                type="button"
                className={styles.switchLink}
                onClick={onSwitchAccount}
              >
                Not you?
              </button>
            </div>
          )}

          <InvitePreview
            projectName={projectName}
            role={role}
            invitedByEmail={load.summary.invited_by_email}
            expiresAt={load.summary.expires_at}
            mode="existing"
          />

          {!signedIn && (
            <div style={{ marginTop: 16, marginBottom: 4 }}>
              <Alert variant="info" title="Sign in to accept">
                We already have an account for {load.summary.email}. Sign in to
                finish joining this project.
              </Alert>
            </div>
          )}

          {signedIn && !emailMatch && (
            <div style={{ marginTop: 16, marginBottom: 4 }}>
              <Alert variant="warn" title="Account mismatch">
                You're signed in as {me!.email}, but this invite is for{" "}
                {load.summary.email}. Switch account to accept.
              </Alert>
            </div>
          )}

          <div className={styles.stack} style={{ marginTop: 18 }}>
            {signedIn && emailMatch ? (
              <>
                <Button
                  size="lg"
                  className={styles.submit}
                  onClick={onAccept}
                  loading={submitting}
                  disabled={submitting}
                >
                  Add {projectName} to my account
                </Button>
                <Button
                  variant="ghost"
                  size="lg"
                  className={styles.submit}
                  onClick={onDecline}
                  disabled={submitting}
                >
                  Decline
                </Button>
              </>
            ) : signedIn && !emailMatch ? (
              <Button
                size="lg"
                className={styles.submit}
                onClick={onSwitchAccount}
              >
                Switch account
              </Button>
            ) : (
              <Button
                size="lg"
                className={styles.submit}
                onClick={() =>
                  navigate(`/login?next=${encodeURIComponent(nextPath)}`)
                }
              >
                Go to sign in
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Variant A — New user signup
  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setActionError(null);
    setSubmitting(true);
    try {
      const r = await apiClient.post<{ csrf_token?: string }>(
        "/api/admin/accept",
        { id, token, name, password },
      );
      // Backend returns 200 with a fresh session cookie + CSRF token — the new
      // account is auto-logged-in. Adopt the CSRF token and go to the app.
      if (r.data?.csrf_token) {
        apiClient.setCSRF(r.data.csrf_token);
      }
      navigate("/projects", { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        setActionError(describeError(err.code));
      } else {
        setActionError("Something went wrong. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  const signupDisabled = submitting || password.length < 12 || name.trim().length < 2;

  return (
    <div className={styles.page}>
      <div className={`${styles.card} ${styles.cardWide}`}>
        <BrandMark />
        <h1 className={styles.title}>You're invited to {projectName}</h1>
        <div className={styles.subtitle}>
          Create an account to access this project.
        </div>

        <div style={{ marginBottom: 22 }}>
          <InvitePreview
            projectName={projectName}
            role={role}
            invitedByEmail={load.summary.invited_by_email}
            expiresAt={load.summary.expires_at}
            mode="new"
          />
        </div>

        {actionError && (
          <div style={{ marginBottom: 14 }}>
            <Alert variant="danger" title="Couldn't accept invite">
              {actionError}
            </Alert>
          </div>
        )}

        <form className={styles.form} onSubmit={onSubmit} noValidate>
          <Input
            type="email"
            label="Email"
            hintRight="from invite — can't change"
            value={load.summary.email}
            readOnly
            // The backing input is also disabled-styled via Input's `readOnly`
            // rule; we leave it interactive (not disabled) so screen readers
            // can still announce the value when focused.
          />
          <Input
            type="text"
            label="Display name"
            autoComplete="name"
            required
            value={name}
            disabled={submitting}
            onChange={(e) => setName(e.target.value)}
            hint="Shown next to your activity. You can change it later."
          />
          <div>
            <Input
              type={showPw ? "text" : "password"}
              label="Choose a password"
              hintRight="min 12 characters"
              autoComplete="new-password"
              required
              value={password}
              disabled={submitting}
              onChange={(e) => setPassword(e.target.value)}
              trailingIcon={
                <button
                  type="button"
                  className={styles.toggleVis}
                  aria-label={showPw ? "Hide password" : "Show password"}
                  onClick={() => setShowPw((v) => !v)}
                  tabIndex={-1}
                >
                  {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              }
            />
            <PasswordStrengthMeter password={password} showRequirements={false} />
          </div>
          <Button
            type="submit"
            size="lg"
            className={styles.submit}
            disabled={signupDisabled}
            loading={submitting}
          >
            {submitting ? "Creating account…" : `Create account & join ${projectName}`}
          </Button>
        </form>

        <div className={styles.footer}>
          By creating an account, you agree to the platform policy.
        </div>
      </div>
    </div>
  );
}

interface InvitePreviewProps {
  projectName: string;
  role: string;
  invitedByEmail?: string | null;
  expiresAt?: string | null;
  mode: "new" | "existing";
}

function InvitePreview({
  projectName,
  role,
  invitedByEmail,
  expiresAt,
  mode,
}: InvitePreviewProps) {
  const roleCopy =
    role === "super_admin"
      ? "platform-wide super admin — manage projects, members, and tokens."
      : "Member — full access to project config, team, and API keys.";

  const expiresHuman = expiresAt ? humanizeExpiry(expiresAt) : null;

  return (
    <div className={styles.invitePreview}>
      <div className={styles.top}>
        <Avatar name={projectName} size={40} />
        <div className={styles.projMeta}>
          <div className={styles.projName}>{projectName}</div>
          <div className={styles.projRole}>
            {mode === "new" ? "You'll join as " : "Adding as "}
            <strong style={{ color: "var(--text-main)" }}>
              {role === "super_admin" ? "Super admin" : "Member"}
            </strong>
            {mode === "new" ? ` — ${roleCopy.split(" — ")[1] ?? roleCopy}` : ""}
          </div>
        </div>
        <Badge variant="accent">invite</Badge>
      </div>
      {(invitedByEmail || expiresHuman) && (
        <div className={styles.invitedBy}>
          {invitedByEmail && (
            <>
              <Avatar name={invitedByEmail} size={22} />
              <span>
                Invited by <strong>{invitedByEmail}</strong>
                {expiresHuman ? ` · expires in ${expiresHuman}` : ""}
              </span>
            </>
          )}
          {!invitedByEmail && expiresHuman && (
            <span>Expires in {expiresHuman}</span>
          )}
        </div>
      )}
    </div>
  );
}

function humanizeExpiry(isoExpiresAt: string): string | null {
  const t = Date.parse(isoExpiresAt);
  if (!Number.isFinite(t)) return null;
  const ms = t - Date.now();
  if (ms <= 0) return null;
  const hours = Math.round(ms / (1000 * 60 * 60));
  if (hours < 1) {
    const minutes = Math.max(1, Math.round(ms / (1000 * 60)));
    return `${minutes}m`;
  }
  if (hours < 48) return `${hours}h`;
  const days = Math.round(hours / 24);
  return `${days}d`;
}
